import type { PaymentRequest, PaymentRequestPaymentMethodEvent, Stripe, StripeConstructor } from "@stripe/stripe-js";

// Stripe.js must be loaded via script tag for PCI compliance
declare global {
  interface Window {
    Stripe?: StripeConstructor;
  }
}

export interface PaymentRequestConfig {
  country: string;
  currency: string;
  total: { label: string; amount: number };
  requestPayerName: boolean;
  requestPayerEmail: boolean;
}

export type ShowPaymentSheetResult =
  | {
      success: true;
      paymentMethodId: string;
      payerEmail?: string;
      complete: (status: "success" | "fail") => void;
    }
  | { success: false; cancelled: true }
  | { success: false; error: string };

export type ConfirmResult = { success: true } | { success: false; error: string };

export enum GooglePayMockScenario {
  None = "none",
  Success = "success",
  Cancelled = "cancelled",
}

export class StripeGooglePayAdapter {
  private stripe: Stripe | null = null;
  private paymentRequest: PaymentRequest | null = null;
  private mockScenario: GooglePayMockScenario;

  constructor(mockScenario?: GooglePayMockScenario) {
    this.mockScenario = mockScenario ?? GooglePayMockScenario.None;
  }

  initialize(publishableKey: string): boolean {
    switch (this.mockScenario) {
      case GooglePayMockScenario.None: {
        console.log("[GooglePay] initialize called");
        if (!window.Stripe) {
          console.log("[GooglePay] Stripe.js not loaded");
          return false;
        }
        this.stripe = window.Stripe(publishableKey);
        const success = this.stripe !== null;
        console.log("[GooglePay] initialize result:", success);
        return success;
      }
      case GooglePayMockScenario.Success:
      case GooglePayMockScenario.Cancelled: {
        console.log("[MockGooglePay] initialize called");
        console.log("[MockGooglePay] initialize result:", true);
        return true;
      }
    }
  }

  createPaymentRequest(config: PaymentRequestConfig): void {
    switch (this.mockScenario) {
      case GooglePayMockScenario.None: {
        console.log("[GooglePay] createPaymentRequest called", config);
        if (!this.stripe) {
          throw new Error("Stripe not initialized");
        }
        this.paymentRequest = this.stripe.paymentRequest(config);
        console.log("[GooglePay] paymentRequest created");
        return;
      }
      case GooglePayMockScenario.Success:
      case GooglePayMockScenario.Cancelled: {
        console.log("[MockGooglePay] createPaymentRequest called", config);
        return;
      }
    }
  }

  async canMakePayment(): Promise<boolean> {
    switch (this.mockScenario) {
      case GooglePayMockScenario.None: {
        console.log("[GooglePay] canMakePayment called");
        if (!this.paymentRequest) {
          console.log("[GooglePay] canMakePayment: no paymentRequest");
          return false;
        }
        const result = await this.paymentRequest.canMakePayment();
        const isAvailable = result?.googlePay ?? false;
        console.log("[GooglePay] canMakePayment result:", { result, isAvailable });
        return isAvailable;
      }
      case GooglePayMockScenario.Success:
      case GooglePayMockScenario.Cancelled: {
        console.log("[MockGooglePay] canMakePayment called");
        console.log("[MockGooglePay] canMakePayment result:", { result: { googlePay: true }, isAvailable: true });
        return true;
      }
    }
  }

  async showPaymentSheet(): Promise<ShowPaymentSheetResult> {
    switch (this.mockScenario) {
      case GooglePayMockScenario.None: {
        console.log("[GooglePay] showPaymentSheet called");
        if (!this.paymentRequest) {
          console.log("[GooglePay] showPaymentSheet: no paymentRequest");
          return { success: false, error: "Payment request not created" };
        }

        return new Promise((resolve) => {
          // biome-ignore lint/style/noNonNullAssertion: checked above
          this.paymentRequest!.on("paymentmethod", (event: PaymentRequestPaymentMethodEvent) => {
            console.log("[GooglePay] showPaymentSheet: paymentmethod event", {
              paymentMethodId: event.paymentMethod.id,
              payerEmail: event.payerEmail,
            });
            resolve({
              success: true,
              paymentMethodId: event.paymentMethod.id,
              payerEmail: event.payerEmail ?? undefined,
              complete: (status) => {
                console.log("[GooglePay] complete called:", status);
                event.complete(status);
              },
            });
          });

          // biome-ignore lint/style/noNonNullAssertion: checked above
          this.paymentRequest!.on("cancel", () => {
            console.log("[GooglePay] showPaymentSheet: cancelled by user");
            resolve({ success: false, cancelled: true });
          });

          console.log("[GooglePay] showing payment sheet...");
          // biome-ignore lint/style/noNonNullAssertion: checked above
          this.paymentRequest!.show();
        });
      }
      case GooglePayMockScenario.Success: {
        console.log("[MockGooglePay] showPaymentSheet: success");
        await new Promise((resolve) => setTimeout(resolve, 500));
        return {
          success: true,
          paymentMethodId: "pm_mock_123456789",
          payerEmail: "mock@example.com",
          complete: (status) => console.log(`[MockGooglePay] complete: ${status}`),
        };
      }
      case GooglePayMockScenario.Cancelled: {
        console.log("[MockGooglePay] showPaymentSheet: cancelled");
        await new Promise((resolve) => setTimeout(resolve, 500));
        return { success: false, cancelled: true };
      }
    }
  }

  async confirmCardSetup(clientSecret: string, paymentMethodId: string): Promise<ConfirmResult> {
    switch (this.mockScenario) {
      case GooglePayMockScenario.None: {
        console.log("[GooglePay] confirmCardSetup called", {
          clientSecret: `${clientSecret.slice(0, 20)}...`,
          paymentMethodId,
        });
        if (!this.stripe) {
          console.log("[GooglePay] confirmCardSetup: Stripe not initialized");
          return { success: false, error: "Stripe not initialized" };
        }

        const { error } = await this.stripe.confirmCardSetup(clientSecret, {
          payment_method: paymentMethodId,
        });

        if (error) {
          console.log("[GooglePay] confirmCardSetup error:", error);
          return { success: false, error: error.message ?? "Card setup failed" };
        }

        console.log("[GooglePay] confirmCardSetup success");
        return { success: true };
      }
      case GooglePayMockScenario.Success: {
        console.log("[MockGooglePay] confirmCardSetup called", { clientSecret, paymentMethodId });
        return { success: true };
      }
      case GooglePayMockScenario.Cancelled: {
        throw new Error("confirmCardSetup should not be called when scenario is Cancelled");
      }
    }
  }
}
